import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider } from "./context/ConfigContext";

import Navbar from './components/Navbar';
import ChatbotButton from './components/ChatbotButton';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// ====== Páginas de Usuario ======
import Home from './Pages/Usuario/home';
import About from './Pages/Usuario/about';
import Agenda from './Pages/Usuario/agenda';
import Login from './Pages/Usuario/login';
import Registro from './Pages/Usuario/registro';
import NotFound from './Pages/Usuario/notfound';
import Servicios from './Pages/Usuario/servicios';
import Ubicación from './Pages/Usuario/ubicacion';
import Contacto from './Pages/Usuario/contacto';
import FAQPage from './Pages/faq/FAQPage';
import PagoExitoso from "./Pages/Usuario/PagoExitoso";
import Terminos from "./Pages/Terminos";

// ====== Recuperación de Contraseña ======
import Recuperar from './Pages/Usuario/recuperar';
import VerificarCodigo from './Pages/Usuario/verificarCodigo';
import NuevaContraseña from './Pages/Usuario/nuevaContraseña';

// ====== Páginas de Admin ======
import AdminLayout from './components/AdminLayout';
import Dashboard from './Pages/Admin/dashboard';
import Pacientes from './Pages/Admin/pacientes';
import AdminAgendaPage from './Pages/Admin/agenda/AdminAgendaPage';
import FAQAdminPage from './Pages/Admin/faq/FAQAdminPage';
import RolesAdminPage from './Pages/Admin/roles/RolesAdminPage';
import CuponesAdminPage from './Pages/Admin/Cupones/CuponesAdminPage';
import EditarSitio from './Pages/Admin/EditarSitio';
import WorkersPerformance from './Pages/Admin/WorkersPerformance';

// ====== Páginas de Paciente ======
import Perfil from './Pages/Paciente/perfil';
import Sesiones from './Pages/Paciente/sesiones';
import Pagos from './Pages/Paciente/pagos';
import CompletarPerfil from './Pages/Paciente/CompletarPerfil';
import CompletarPareja from './Pages/Paciente/CompletarPareja';

// ====== Páginas de Trabajador ======
import WorkerLayout from './components/WorkerLayout';
import TrabajadorAgendaPage from './Pages/Trabajador/agenda/TrabajadorAgendaPage';
import TrabajadorPacientes from './Pages/Trabajador/pacientes';
import TrabajadorSeguimiento from './Pages/Trabajador/seguimiento';
import TrabajadorDesempeno from './Pages/Trabajador/desempeno';

// ====== Firebase Config ======
import { auth } from './firebase/config';

// ====== Rutas protegidas ======
import RequireRole from './components/RequireRole';

// ==================================================
// COMPONENTE DE RUTA PROTEGIDA
// ==================================================
const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  if (roles && roles.length > 0) {
    const permitidos = Array.isArray(roles) ? roles : [roles];
    if (!permitidos.includes(user.rol)) {
      return <Navigate to="/notfound" replace />;
    }
  }

  return children;
};

// ==================================================
// SHELL PRINCIPAL DE LA APP
// ==================================================
function AppShell() {
  const location = useLocation();
  const { user } = useAuth();

  // Ocultamos Footer/Chatbot en rutas admin o trabajador
  const isAdminRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/trabajador');

  // ========== PADDING INTELIGENTE FINAL ==========

  // Páginas con Hero
  const heroPages = ['/', '/servicios', '/about'];
  const isHeroPage = heroPages.includes(location.pathname);

  const contentPad = isAdminRoute
    ? 'pt-[4rem] md:pt-[6rem]'          // Admin → Ajuste perfecto móvil + desktop
    : isHeroPage
    ? 'pt-[7rem] md:pt-[10rem]'         // Hero público
    : 'pt-[7rem] md:pt-[10rem]';        // Resto del público
       

  // Redirección a completar perfil
  if (
    user?.rol === 'usuario' &&
    user?.perfilIncompleto &&
    location.pathname !== '/completar-perfil'
  ) {
    return <Navigate to="/completar-perfil" replace />;
  }

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className={`min-h-screen ${contentPad}`}>
        <Routes>
          {/* Público */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/completar-perfil" element={<CompletarPerfil />} />
          <Route path="/servicios" element={<Servicios />} />
          <Route path="/ubicacion" element={<Ubicación />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/pago-exitoso" element={<PagoExitoso />} />
          <Route path="/terminos" element={<Terminos />} />

          {/* Recuperación */}
          <Route path="/recuperar" element={<Recuperar />} />
          <Route path="/verificar-codigo" element={<VerificarCodigo />} />
          <Route path="/nueva-contraseña" element={<NuevaContraseña />} />

          {/* Paciente */}
          <Route
            path="/paciente/perfil"
            element={
              <ProtectedRoute roles={['usuario', 'admin']}>
                <Perfil />
              </ProtectedRoute>
            }
          />

          <Route
            path="/paciente/completar-pareja/:planId"
            element={
              <ProtectedRoute roles={['usuario', 'admin']}>
                <CompletarPareja />
              </ProtectedRoute>
            }
          />

          <Route
            path="/paciente/sesiones"
            element={
              <ProtectedRoute roles={['usuario', 'admin']}>
                <Sesiones />
              </ProtectedRoute>
            }
          />

          <Route
            path="/paciente/pagos"
            element={
              <ProtectedRoute roles={['usuario', 'admin']}>
                <Pagos />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/*"
            element={
              <RequireRole allow={['admin']}>
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="agenda" replace />} />
            <Route path="agenda" element={<AdminAgendaPage />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="pacientes" element={<Pacientes />} />
            <Route path="faq" element={<FAQAdminPage />} />
            <Route path="roles" element={<RolesAdminPage />} />
            <Route path="cupones" element={<CuponesAdminPage />} />
            <Route path="editarsitio" element={<EditarSitio />} />
            <Route path="desempeno-trabajadores" element={<WorkersPerformance />} />
            <Route path="*" element={<Navigate to="agenda" replace />} />
          </Route>

          {/* Trabajador */}
          <Route
            path="/trabajador/*"
            element={
              <RequireRole allow={['worker', 'admin']}>
                <WorkerLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="agenda" replace />} />
            <Route path="agenda" element={<TrabajadorAgendaPage />} />
            <Route path="pacientes" element={<TrabajadorPacientes />} />
            <Route path="seguimiento" element={<TrabajadorSeguimiento />} />
            <Route path="desempeno" element={<TrabajadorDesempeno />} />

          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Footer público */}
        {!isAdminRoute && <ChatbotButton />}
        {!isAdminRoute && <Footer />}
      </div>
    </>
  );
}

// ==================================================
// APP PRINCIPAL
// ==================================================
function App() {
  
  useEffect(() => {
    try {
      console.log('🔎 Firebase conectado a:');
      console.log('  projectId:', auth.app?.options?.projectId);
      console.log('  storageBucket:', auth.app?.options?.storageBucket);
    } catch (e) {
      console.warn('No se pudo leer la app de Firebase para debug:', e);
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ConfigProvider>      
          <AppShell />
        </ConfigProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
